import { useSearch } from "wouter";
import type { MeInstance } from "@shared/types";
import { pendingInstanceFrom } from "@/contexts/TenantInstanceContext";
import { routeFor } from "@/lib/routes";

/**
 * Where the instance picker returns to (notification PR review, behaviour finding 2).
 *
 * A location that needs an instance, opened with none selected, is redirected to the picker. A
 * notification's run link (`/runs/{runId}?instance={id}`) opened in a fresh browser used to lose
 * the run there: the picker landed on the first allowed screen. The redirect now carries the
 * requested location as `returnTo`, the picker goes back to it after the pick, and when the link
 * names an instance the user can open there is no choice to make: it is selected and the user is
 * returned at once. The run page then sees its own instance and drops the parameter (R12).
 */

const PICKER_PATH = "/instances";
const RETURN_TO_PARAM = "returnTo";
/** Only used to resolve and check a relative address; never requested. */
const LOCAL_ORIGIN = "https://skydata.invalid";

/** The picker's address for a location that needs an instance, carrying where to return. */
export function pickerPathFor(location: string, search: string): string {
  const target = search ? `${location}?${search.replace(/^\?/, "")}` : location;
  if (safeReturnTo(target) === null || target === "/") return PICKER_PATH;
  return `${PICKER_PATH}?${new URLSearchParams({ [RETURN_TO_PARAM]: target }).toString()}`;
}

/**
 * A `returnTo` value that is an address inside this app, or null. An absolute URL, a
 * protocol-relative one (`//host`), a backslash form browsers read as one, and the picker itself
 * are refused, so the parameter can never send anyone off the site or into a loop.
 */
export function safeReturnTo(raw: string | null): string | null {
  if (!raw || !raw.startsWith("/") || raw.startsWith("//") || raw.startsWith("/\\")) return null;
  let url: URL;
  try {
    url = new URL(raw, LOCAL_ORIGIN);
  } catch {
    return null;
  }
  if (url.origin !== LOCAL_ORIGIN || url.pathname === PICKER_PATH) return null;
  return `${url.pathname}${url.search}`;
}

/** Whether an instance's own permissions reach the route an address names (FR-13). */
export function instanceCanOpen(instance: MeInstance, address: string): boolean {
  const needed = routeFor(address)?.anyPermission;
  if (!needed || needed.length === 0) return true;
  return needed.some((permission) => instance.permissions.includes(permission));
}

/**
 * The picker's `returnTo`, and the instance its link names when the user can open that address
 * in it (`linked`), else null.
 */
export function useReturnTo(instances: readonly MeInstance[]): {
  returnTo: string | null;
  linked: MeInstance | null;
} {
  const returnTo = safeReturnTo(new URLSearchParams(useSearch()).get(RETURN_TO_PARAM));
  if (!returnTo) return { returnTo: null, linked: null };
  const cut = returnTo.indexOf("?");
  const pending = pendingInstanceFrom(cut === -1 ? "" : returnTo.slice(cut));
  const linked = pending
    ? (instances.find(
        (instance) => instance.id.toLowerCase() === pending && instanceCanOpen(instance, returnTo),
      ) ?? null)
    : null;
  return { returnTo, linked };
}
